import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import { useIsAdmin, useMe } from '../../../api/session';
import type { TicketDetail } from '../../../api/ticket-types';
import { Badge, StatusBadge } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { Timestamp } from '../../../ui/Timestamp';
import { Alert } from '../../../ui/feedback';
import { ActorName, Card } from '../../../ui/misc';
import { RelationshipForm } from './RelationshipForm';
import { ReasonDialog } from './dialogs';

export function Relationships({ t }: { t: TicketDetail }) {
  const admin = useIsAdmin();
  const me = useMe();
  const isReporter = t.reporter.kind === 'human' && t.reporter.id === me.user.id;
  const [form, setForm] = useState<
    null | 'parent' | 'dependency' | 'related' | 'blocker' | 'decision' | 'clarify'
  >(null);
  const [override, setOverride] = useState<{
    dependencyId?: string;
    manualBlockerId?: string;
    label: string;
  } | null>(null);
  const [clear, setClear] = useState<string | null>(null);
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const inv = [
    ['ticket', t.project.key, t.number],
    ['board', t.project.key],
  ];
  const removeDep = useAction(
    (d: { id: string; reason: string }) =>
      api.del(`${base}/dependencies/${d.id}?reason=${encodeURIComponent(d.reason)}`),
    { success: 'Dependency removed', invalidate: inv },
  );
  const removeRelated = useAction((id: string) => api.del(`${base}/related/${id}`), {
    success: 'Link removed',
    invalidate: inv,
  });
  const editable = !t.project.archived;
  const rejectedChildren = t.descendants.filter((d) => d.status === 'closed' && d.resolution === 'rejected');
  const row = (
    k: {
      key: string;
      number: number;
      title: string;
      status: TicketDetail['status'];
      resolution: TicketDetail['resolution'];
    },
    extra?: React.ReactNode,
  ) => (
    <li key={k.key} style={{ justifyContent: 'space-between' }}>
      <span style={{ minWidth: 0 }}>
        <Link to={`/projects/${t.project.key}/tickets/${k.number}`} className="mono">
          {k.key}
        </Link>{' '}
        {k.title}
      </span>
      <span className="row nowrap">
        <StatusBadge status={k.status} resolution={k.resolution} />
        {extra}
      </span>
    </li>
  );
  return (
    <Card
      title="Relationships and blockers"
      subtitle="Structure is part of accepted scope; admins change it. Blocked and clarification are indicators, not columns."
      action={
        editable && (
          <div className="row">
            {admin && (
              <Button size="sm" variant="secondary" onClick={() => setForm('parent')}>
                Set parent
              </Button>
            )}
            {admin && (
              <Button size="sm" variant="secondary" onClick={() => setForm('dependency')}>
                Add blocker link
              </Button>
            )}
            {t.viewer.canComment && (
              <Button size="sm" variant="ghost" onClick={() => setForm('related')}>
                Relate
              </Button>
            )}
            {admin && (
              <Button size="sm" variant="ghost" onClick={() => setForm('blocker')}>
                Record blocker
              </Button>
            )}
            {(admin || isReporter) && (
              <Button size="sm" variant="ghost" onClick={() => setForm('clarify')}>
                {t.needsClarification ? 'Resolve clarification' : 'Needs clarification'}
              </Button>
            )}
          </div>
        )
      }
    >
      <div className="grid-2">
        <div className="stack sm">
          <span className="small strong">Parent</span>
          <ul className="checklist">{t.parent ? row(t.parent) : <li className="small muted">None</li>}</ul>
          <span className="small strong">Children</span>
          <ul className="checklist">
            {t.children.length ? t.children.map((c) => row(c)) : <li className="small muted">None</li>}
          </ul>
          <span className="small strong">Related</span>
          <ul className="checklist">
            {t.related.length ? (
              t.related.map((r) =>
                row(
                  r,
                  editable && t.viewer.canComment && (
                    <Button size="sm" variant="ghost" onClick={() => removeRelated.mutate(r.linkId)}>
                      Unlink
                    </Button>
                  ),
                ),
              )
            ) : (
              <li className="small muted">None</li>
            )}
          </ul>
        </div>
        <div className="stack sm">
          <span className="small strong">Blocked by</span>
          <ul className="checklist">
            {t.prerequisites.length ? (
              t.prerequisites.map((p) =>
                row(
                  p,
                  admin && editable && (
                    <>
                      {!t.blockers.find((b) => b.id === p.dependencyId)?.overridden &&
                        !(p.status === 'closed' && p.resolution === 'done') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setOverride({ dependencyId: p.dependencyId, label: `dependency on ${p.key}` })
                            }
                          >
                            Override
                          </Button>
                        )}
                      <Button size="sm" variant="ghost" onClick={() => setClear(`dep:${p.dependencyId}`)}>
                        Remove
                      </Button>
                    </>
                  ),
                ),
              )
            ) : (
              <li className="small muted">None</li>
            )}
          </ul>
          <span className="small strong">Blocking</span>
          <ul className="checklist">
            {t.dependents.length ? t.dependents.map((d) => row(d)) : <li className="small muted">None</li>}
          </ul>
          <span className="small strong">Manual blockers</span>
          <ul className="checklist">
            {t.manualBlockers.filter((b) => !b.clearedAt).length ? (
              t.manualBlockers
                .filter((b) => !b.clearedAt)
                .map((b) => {
                  const info = t.blockers.find((x) => x.id === b.id);
                  return (
                    <li key={b.id} style={{ justifyContent: 'space-between' }}>
                      <span>
                        {b.reason} {info?.overridden && <Badge tone="info">Overridden</Badge>}
                      </span>
                      {admin && editable && (
                        <span className="row nowrap">
                          {!info?.overridden && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setOverride({ manualBlockerId: b.id, label: 'manual blocker' })}
                            >
                              Override
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setClear(`blk:${b.id}`)}>
                            Clear
                          </Button>
                        </span>
                      )}
                    </li>
                  );
                })
            ) : (
              <li className="small muted">None</li>
            )}
          </ul>
          {t.overrides.filter((o) => o.active).length > 0 && (
            <>
              <span className="small strong">Active overrides (scope revision {t.scopeRevision})</span>
              <ul className="checklist">
                {t.overrides
                  .filter((o) => o.active)
                  .map((o) => (
                    <li key={o.id} className="small">
                      “{o.reason}” — <ActorName actor={o.admin} />, <Timestamp value={o.createdAt} />
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      </div>
      {rejectedChildren.length > 0 && (
        <Alert
          tone={rejectedChildren.every((c) => c.scopeDecisionRecorded) ? 'info' : 'warning'}
          title="Rejected children"
        >
          <ul>
            {rejectedChildren.map((c) => (
              <li key={c.ticketId}>
                {c.key}:{' '}
                {c.scopeDecisionRecorded
                  ? 'parent-scope decision recorded'
                  : 'needs an admin parent-scope decision'}
              </li>
            ))}
          </ul>
          {admin && editable && (
            <Button size="sm" variant="secondary" onClick={() => setForm('decision')}>
              Record parent-scope decision
            </Button>
          )}
          {t.scopeDecisions
            .filter((d) => d.parentTicketId === t.id)
            .map((d) => (
              <span key={d.id} className="small">
                {d.child?.key}: “{d.decision}” — <ActorName actor={d.admin} />
              </span>
            ))}
        </Alert>
      )}
      {form && <RelationshipForm t={t} kind={form} onClose={() => setForm(null)} />}
      {override && (
        <ReasonDialog
          title={`Override ${override.label}?`}
          description={`Applies only to this ${override.label} at scope revision ${t.scopeRevision}. The prerequisite stays visible and is not marked done.`}
          confirm="Record override"
          onClose={() => setOverride(null)}
          run={(reason) => api.post(`${base}/overrides`, { ...override, reason })}
          invalidate={inv}
        />
      )}
      {clear && (
        <ReasonDialog
          title={clear.startsWith('dep:') ? 'Remove blocking dependency?' : 'Clear blocker?'}
          description={
            clear.startsWith('dep:')
              ? 'Dependencies are part of scope; the ticket gets a new scope revision. Accepted tickets must be reconsidered first.'
              : 'The blocker is cleared with your reason.'
          }
          confirm={clear.startsWith('dep:') ? 'Remove dependency' : 'Clear blocker'}
          onClose={() => setClear(null)}
          run={(reason) =>
            clear.startsWith('dep:')
              ? removeDep.mutateAsync({ id: clear.slice(4), reason })
              : api.post(`${base}/blockers/${clear.slice(4)}/clear`, { reason })
          }
          invalidate={inv}
        />
      )}
    </Card>
  );
}
